import { supabase } from '../lib/supabase';

/**
 * Audit Log Service
 * Tracks all admin actions: logins, exports, settings changes, user edits
 */

export const AUDIT_ACTIONS = {
  // Auth
  LOGIN: 'admin_login',
  LOGOUT: 'admin_logout',
  LOGIN_FAILED: 'admin_login_failed',
  // Dashboard
  EXPORT_DASHBOARD: 'export_dashboard',
  // Settings
  SETTINGS_CHANGE: 'settings_change',
  MAINTENANCE_MODE_ON: 'maintenance_mode_enabled',
  MAINTENANCE_MODE_OFF: 'maintenance_mode_disabled',
  // User management
  USER_EDIT: 'user_edit',
  USER_DEACTIVATE: 'user_deactivate',
  USER_ACTIVATE: 'user_activate',
  USER_ROLE_CHANGE: 'user_role_change',
  // Driver management
  DRIVER_APPROVE: 'driver_approve',
  DRIVER_REJECT: 'driver_reject',
  DRIVER_SUSPEND: 'driver_suspend',
  // Promotions
  PROMO_CREATE: 'promo_code_created',
  PROMO_UPDATE: 'promo_code_updated',
  PROMO_DELETE: 'promo_code_deleted',
  // Disputes
  TICKET_RESOLVE: 'ticket_resolved',
  TICKET_ASSIGN: 'ticket_assigned',
  // Payouts
  PAYOUT_APPROVE: 'payout_approved',
  PAYOUT_REJECT: 'payout_rejected',
};

/**
 * Log an admin action to the audit trail
 * @param {object} params
 * @param {string} params.actionType - One of AUDIT_ACTIONS values
 * @param {string} [params.actorName] - Display name of the admin
 * @param {string} [params.targetTable] - Table affected
 * @param {string} [params.targetId] - UUID of the affected record
 * @param {string} [params.notes] - Human-readable description
 * @param {object} [params.metadata] - Extra context (JSON)
 */
export const logAdminAction = async ({
  actionType,
  actorName = '',
  targetTable = null,
  targetId = null,
  notes = '',
  metadata = {},
}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    const { error } = await supabase?.from('admin_actions')?.insert({
      admin_id: user?.id,
      action_type: actionType,
      actor_name: actorName || user?.email?.split('@')?.[0] || 'Admin',
      target_table: targetTable,
      target_id: targetId || null,
      notes: notes,
      metadata: metadata,
      ip_hint: null,
    });

    if (error) {
      console.warn('Audit log insert failed:', error?.message);
    }
  } catch (err) {
    console.warn('Audit log error:', err?.message);
  }
};

/**
 * Fetch audit log entries (admin only)
 * @param {object} options
 * @param {number} [options.limit=50]
 * @param {number} [options.offset=0]
 * @param {string} [options.actionType] - Filter by action type
 * @param {string} [options.search] - Search in notes/actor_name
 */
export const fetchAuditLogs = async ({ limit = 50, offset = 0, actionType = '', search = '' } = {}) => {
  let query = supabase
    ?.from('admin_actions')
    ?.select('*')
    ?.order('created_at', { ascending: false })
    ?.range(offset, offset + limit - 1);

  if (actionType) {
    query = query?.eq('action_type', actionType);
  }

  if (search) {
    query = query?.or(`notes.ilike.%${search}%,actor_name.ilike.%${search}%,action_type.ilike.%${search}%`);
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { logs: data || [], count };
};
